import type { Card, CardFilters, UserFilters } from '@/types'
import { buildHiddenPredicates, buildRangePredicates, buildSelectionPredicates } from './cardPredicates'
import { getCardFuseIndex } from './fuseIndex'
import { sortByRelevance } from './sortByRelevance'

/**
 * Fuzzy-search matches + relevance order (order only when no explicit sort).
 * Name matches come from Fuse (typo-tolerant, word-anchored via the tight
 * threshold on getCardFuseIndex); card_text matches are a plain
 * case-insensitive substring check so a correctly-spelled word like "banish"
 * always matches without dragging in unrelated cards the way fuzzy-matching
 * long ability text would. Name matches rank first, text-only matches after.
 */
function computeSearchMatch(
  cards: Card[],
  filters: CardFilters,
): { matchIds: Set<number> | null; orderMap: Map<number, number> | null } {
  if (!filters.q) return { matchIds: null, orderMap: null }

  const fuse = getCardFuseIndex(cards)
  const nameResults = fuse.search(filters.q)
  const matchIds = new Set(nameResults.map((r) => r.item.id))
  const orderMap = filters.sort ? null : new Map(nameResults.map((r, i) => [r.item.id, i]))

  const query = filters.q.toLowerCase()
  for (const card of cards) {
    if (matchIds.has(card.id)) continue
    if (card.cardText?.toLowerCase().includes(query)) {
      matchIds.add(card.id)
      orderMap?.set(card.id, orderMap.size)
    }
  }

  return { matchIds, orderMap }
}

export function filterCards(
  cards: Card[],
  filters: CardFilters,
  collectionQuantities?: Record<number, number>,
  userFilters?: UserFilters,
  showHidden?: boolean,
): Card[] {
  const { matchIds, orderMap } = computeSearchMatch(cards, filters)

  const predicates = buildHiddenPredicates(userFilters, showHidden)
  if (matchIds) predicates.push((c) => matchIds.has(c.id))
  predicates.push(...buildSelectionPredicates(filters), ...buildRangePredicates(filters, collectionQuantities))

  const result = cards.filter((c) => predicates.every((matches) => matches(c)))

  // Preserve search relevance order when no explicit sort
  if (orderMap) {
    sortByRelevance(result, orderMap)
  }

  return result
}
